import { computed, inject, Injectable, signal } from '@angular/core';
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import {
  AssetsApiService,
  RoomObject,
  RoomObjectsResponse,
  GetRoomObjectsParams,
  CreateRoomObjectData,
} from './assets-api.service';

export const assetKeys = {
  all: ['assets'] as const,
  lists: () => [...assetKeys.all, 'list'] as const,
  list: (page: number, perPage: number, search: string, consumable: boolean) =>
    [...assetKeys.lists(), page, perPage, search, consumable] as const,
  details: () => [...assetKeys.all, 'detail'] as const,
  detail: (id: number) => [...assetKeys.details(), id] as const,
};

@Injectable({ providedIn: 'root' })
export class AssetsQueriesService {
  private readonly api = inject(AssetsApiService);
  private readonly queryClient = injectQueryClient();

  readonly currentPage = signal(1);
  readonly perPage = signal(100);
  readonly searchTerm = signal('');
  readonly consumable = signal(false);

  readonly assetsQuery = injectQuery(() => ({
    queryKey: assetKeys.list(
      this.currentPage(),
      this.perPage(),
      this.searchTerm(),
      this.consumable()
    ),
    queryFn: () =>
      this.fetchAssets({
        page: this.currentPage(),
        perPage: this.perPage(),
        search: this.searchTerm() || undefined,
        consumable: this.consumable(),
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  }));

  readonly assets = computed(() => this.assetsQuery.data()?.body ?? []);
  readonly totalRecords = computed(() => this.assetsQuery.data()?.totalRecords ?? 0);
  readonly isLoading = computed(() => this.assetsQuery.isPending());
  readonly isFetching = computed(() => this.assetsQuery.isFetching());
  readonly error = computed(() => this.assetsQuery.error());

  readonly selectedAssetId = signal<number | null>(null);

  readonly selectedAssetQuery = injectQuery(() => ({
    queryKey: assetKeys.detail(this.selectedAssetId() ?? 0),
    queryFn: () => lastValueFrom(this.api.getRoomObject(this.selectedAssetId()!)),
    enabled: this.selectedAssetId() !== null,
    staleTime: 5 * 60 * 1000,
  }));

  readonly selectedAsset = computed(() => this.selectedAssetQuery.data());

  readonly createAssetMutation = injectMutation(() => ({
    mutationFn: (data: CreateRoomObjectData) => lastValueFrom(this.api.createRoomObject(data)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  }));

  readonly updateAssetMutation = injectMutation(() => ({
    mutationFn: ({ id, data }: { id: number; data: Partial<RoomObject> }) =>
      lastValueFrom(this.api.updateRoomObject(id, data)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
      this.queryClient.invalidateQueries({ queryKey: assetKeys.detail(variables.id) });
    },
  }));

  readonly deleteAssetMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.api.inactivateRoomObject(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  }));

  private async fetchAssets(params: GetRoomObjectsParams): Promise<RoomObjectsResponse> {
    return lastValueFrom(this.api.getRoomObjects(params));
  }

  setPage(page: number): void {
    this.currentPage.set(page);
  }

  setSearch(term: string): void {
    this.searchTerm.set(term);
    this.currentPage.set(1);
  }

  setConsumable(value: boolean): void {
    this.consumable.set(value);
    this.currentPage.set(1);
  }

  selectAsset(id: number | null): void {
    this.selectedAssetId.set(id);
  }

  invalidateAssets(): void {
    this.queryClient.removeQueries({ queryKey: assetKeys.lists() });
  }

  invalidateAssetDetail(id: number): void {
    this.queryClient.invalidateQueries({ queryKey: assetKeys.detail(id) });
  }

  async refetch(): Promise<void> {
    await this.assetsQuery.refetch();
  }

  async getAssets(params: GetRoomObjectsParams = {}): Promise<RoomObjectsResponse> {
    return this.fetchAssets(params);
  }

  async getPage(page: number): Promise<RoomObjectsResponse> {
    const cacheKey = assetKeys.list(page, this.perPage(), this.searchTerm(), this.consumable());

    const cachedData = this.queryClient.getQueryData<RoomObjectsResponse>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const data = await this.fetchAssets({
      page,
      perPage: this.perPage(),
      search: this.searchTerm() || undefined,
      consumable: this.consumable(),
    });

    this.queryClient.setQueryData(cacheKey, data);
    return data;
  }

  getAssetById(id: number): RoomObject | undefined {
    return this.assets().find((asset) => asset.id === id);
  }

  resetPagination(): void {
    this.currentPage.set(1);
    this.searchTerm.set('');
  }
}
